import { Button, Divider, NumberInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDeviceFloppy, IconRefresh } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { clientsApi } from "#/api/clientsApi";
import { predictionApi } from "#/api/predictionApi";
import { showErrorNotification, showSuccessNotification } from "#/components/notifications";
import type { Client } from "#/types/client";
import type { PredictionConfig } from "#/types/prediction";
import FieldDescription from "#/components/ui/FieldDescription";
import { logger } from "#/utils/logger";
import PredictionComputeModal, { type ComputeState } from "./PredictionComputeModal";

interface Props {
  clientId: number;
  client: Client;
  onSaved: () => void;
}

/** Defaults as the database has them. */
function configOf(client: Client): PredictionConfig {
  return {
    predictionVisitGapMinutes: client.predictionVisitGapMinutes ?? 240,
    predictionMinDeliveries: client.predictionMinDeliveries ?? 7,
    predictionGroupingLimitMinutes: client.predictionGroupingLimitMinutes ?? 270,
    predictionOffloadingMinutes: client.predictionOffloadingMinutes ?? 30,
  };
}

/** The grouping limit is stored in minutes and edited in hours. */
const toHours = (minutes: number) => Math.round((minutes / 60) * 100) / 100;
const toMinutes = (hours: number) => Math.round(hours * 60);

/**
 * Logistics Prediction settings of one client, plus "Recompute now" and the
 * users' feedback totals. Every field says in one line what it does. The
 * regular-share and stable-quantity cut-offs stay on the client record with
 * their defaults but are not shown: the engine decides with them, nobody tunes
 * them by hand (Ammad, 2026-09-28).
 */
const PredictionSettingsPanel = ({ clientId, client, onSaved }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm<PredictionConfig>({
    initialValues: configOf(client),
    validate: {
      predictionVisitGapMinutes: (value) =>
        Number.isInteger(value) && value >= 1
          ? null
          : t("clients.prediction.settings.invalidPositive"),
      predictionMinDeliveries: (value) =>
        Number.isInteger(value) && value >= 1
          ? null
          : t("clients.prediction.settings.invalidPositive"),
      predictionGroupingLimitMinutes: (value) =>
        Number.isInteger(value) && value >= 1
          ? null
          : t("clients.prediction.settings.invalidPositive"),
      predictionOffloadingMinutes: (value) =>
        Number.isInteger(value) && value >= 0
          ? null
          : t("clients.prediction.settings.invalidNonNegative"),
    },
  });

  useEffect(() => {
    form.setValues(configOf(client));
    form.resetDirty(configOf(client));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client.predictionVisitGapMinutes,
    client.predictionMinDeliveries,
    client.predictionGroupingLimitMinutes,
    client.predictionOffloadingMinutes,
  ]);

  const save = useMutation({
    mutationFn: (values: PredictionConfig) => clientsApi.update(clientId, values),
    onSuccess: () => {
      showSuccessNotification({
        message: t("clients.prediction.settings.saved"),
        color: "green.5",
      });
      onSaved();
    },
    onError: (error: Error) => {
      logger.error("PredictionSettingsPanel save error:", error);
      showErrorNotification({ message: error.message ?? t("common.failedToProcess") });
    },
  });

  const [computeState, setComputeState] = useState<ComputeState>({ kind: "closed" });
  const compute = useMutation({
    mutationFn: () => predictionApi.compute(clientId),
    onMutate: () => setComputeState({ kind: "running" }),
    onSuccess: (response) => {
      setComputeState({ kind: "done", summary: response.data });
      void queryClient.invalidateQueries({ queryKey: ["prediction-board"] });
      void queryClient.invalidateQueries({ queryKey: ["prediction-feedback", clientId] });
    },
    onError: (error: Error) => {
      // The API client already toasts the server message; the modal shows it too.
      logger.error("PredictionSettingsPanel compute error:", error);
      setComputeState({ kind: "failed", error: error.message });
    },
  });

  const feedback = useQuery({
    queryKey: ["prediction-feedback", clientId],
    queryFn: async () => (await predictionApi.feedbackSummary(clientId)).data,
  });

  /** Lead line + bullets from the locale, so long explanations stay readable. */
  const describe = (key: string, values: Record<string, string | number> = {}) => (
    <FieldDescription
      lead={t(`clients.prediction.settings.${key}Lead`, values)}
      points={
        t(`clients.prediction.settings.${key}Points`, {
          ...values,
          returnObjects: true,
        }) as string[]
      }
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <form
        className="flex flex-col gap-4"
        onSubmit={form.onSubmit((values) => save.mutate(values))}
      >
        <NumberInput
          label={t("clients.prediction.settings.visitGap")}
          description={describe("visitGap", { dwell: client.prvMinDwellMinutes ?? 120 })}
          min={1}
          step={30}
          allowDecimal={false}
          {...form.getInputProps("predictionVisitGapMinutes")}
        />
        <NumberInput
          label={t("clients.prediction.settings.minDeliveries")}
          description={describe("minDeliveries")}
          min={1}
          allowDecimal={false}
          {...form.getInputProps("predictionMinDeliveries")}
        />
        <NumberInput
          label={t("clients.prediction.settings.groupingLimit")}
          description={describe("groupingLimit")}
          min={0.5}
          step={0.5}
          decimalScale={2}
          value={toHours(form.values.predictionGroupingLimitMinutes)}
          error={form.errors.predictionGroupingLimitMinutes}
          onChange={(value) =>
            form.setFieldValue(
              "predictionGroupingLimitMinutes",
              typeof value === "number" ? toMinutes(value) : 0
            )
          }
        />
        <NumberInput
          label={t("clients.prediction.settings.offloading")}
          description={describe("offloading")}
          min={0}
          step={5}
          allowDecimal={false}
          {...form.getInputProps("predictionOffloadingMinutes")}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            leftSection={<IconDeviceFloppy size={18} />}
            disabled={!form.isDirty()}
            loading={save.isPending}
          >
            {t("common.save")}
          </Button>
        </div>
      </form>

      <Divider label={t("clients.prediction.compute.title")} labelPosition="left" />
      <div className="flex flex-col gap-2">
        <Button
          fullWidth
          variant="outline"
          leftSection={<IconRefresh size={18} />}
          onClick={() => compute.mutate()}
          loading={compute.isPending}
        >
          {t("clients.prediction.compute.button")}
        </Button>
        <PredictionComputeModal
          clientId={clientId}
          state={computeState}
          onClose={() => setComputeState({ kind: "closed" })}
        />
        <span className="text-xs text-gray-500">{t("clients.prediction.compute.hint")}</span>
      </div>

      <Divider label={t("clients.prediction.feedback.title")} labelPosition="left" />
      <div className="flex flex-col gap-1 text-sm text-gray-700">
        {feedback.data ? (
          <>
            <span>
              {t("clients.prediction.feedback.totals", {
                useful: feedback.data.useful,
                notUseful: feedback.data.notUseful,
              })}
            </span>
            {feedback.data.notUseful > 0 && (
              <ul className="list-disc pl-5 text-xs text-gray-600">
                {Object.entries(feedback.data.reasons)
                  .filter(([, count]) => count > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, count]) => (
                    <li key={reason}>
                      {t(`predictedDeliveries.feedback.reasons.${reason}`)}: {count}
                    </li>
                  ))}
              </ul>
            )}
            <span className="text-xs text-gray-500">{t("clients.prediction.feedback.hint")}</span>
          </>
        ) : (
          <span className="text-xs text-gray-500">--</span>
        )}
      </div>
    </div>
  );
};

export default PredictionSettingsPanel;
